import { useSearchParams } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import { BlockerFeed } from '../components/BlockerFeed';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import {
  type ApiBlocker,
  answerBlocker,
  type BlockerAnswered,
  BlockerAnswerUnreadable,
  type BlockerReply,
  focusBlockers,
  useBlockers,
  withdrawBlocker,
} from '../lib/blockers';
import { createKeyedMutation } from '../lib/keyed-mutation';
import { useKeys } from '../lib/keys';
import { SESSION_TOKEN } from '../lib/session';

// Blockers (plan doc 07 step 5) — the human door onto the answerable hold. Prod
// held 80 open blockers and zero human answers ever, because the only surface was
// an MCP verb that REFUSES a caller with no user principal and says "answer from
// the dashboard": a screen that did not exist. CONTAINER — it owns the read, the
// scope, the deep link and both writes; the feed and the cards paint.
//
// OLDEST FIRST is the port's order and the right one: the longest-owed answer is
// the most overdue. `open` is part of the query key, so widening to the closed
// record re-reads server-side.
//
// `?blocker=<id>` IS A DEEP LINK, minted by the fleet notification
// (`apps/worker/src/fleet/blocker-ingest.ts`). It narrows to that ONE ask —
// dumping 80 rows on somebody who clicked a notification about one is not
// answering it — and an id the current list does not hold gets its own state
// rather than a blank page, because by then it has usually been closed.
//
// NO CLIENT ROLE GATE. `read:tasks` reads and `write:tasks` writes, the same
// scope the queue board's New task spends; a refused write renders the API's own
// 403 in the toast rather than a control that silently was not there.

/**
 * What an answer did, in the words the notice shows. Names the work, because nothing else
 * on screen will: a silent success reads as a dead button. Only the triage arm says
 * "nothing resumed"; a released or linked answer did resume something.
 */
function answeredNotice(done: BlockerAnswered): string {
  switch (done.arm) {
    case 'triage':
      return t('blockers.answered_notice', { id: done.raw_task_id });
    case 'released':
      return t('blockers.released_notice', { id: done.task_id });
    case 'linked':
      return t('blockers.linked_notice');
  }
}

/** The Blockers page: the account's asks, the deep-link focus, and both writes. */
export function Blockers(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session".
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const [params, setParams] = useSearchParams();
  const [openOnly, setOpenOnly] = createSignal(true);
  const [notice, setNotice] = createSignal<string | null>(null);
  const query = useBlockers(openOnly, token);
  // The pool the answer form's optional model field offers (#6521). Its own query, so a
  // refused or slow key read costs the FIELD and never the blocker list: the form simply
  // offers no pick, which is what an account with no keys sees anyway.
  const keys = useKeys(token);

  const focusId = (): string | null =>
    typeof params['blocker'] === 'string' ? params['blocker'] : null;

  // Busy is keyed BY BLOCKER: two cards are two independent calls, and a single
  // slot would re-enable card A the moment card B started — which is why this is
  // `createKeyedMutation` and not `createMutation`, whose lock is per SURFACE.
  // Both acts share ONE key per row, so the call is passed per invocation.
  // `onStart` clears the success notice INSIDE the guard: a second click the
  // guard dropped must not wipe the notice the call still in flight produced.
  const act = createKeyedMutation({
    fallback: t('blockers.action_failed'),
    onStart: () => setNotice(null),
  });

  /** One in-flight call per blocker; the busy flag is released either way. */
  const run = (blocker: ApiBlocker, call: () => Promise<string>): void => {
    void act.run(blocker.id, async () => {
      const message = await call();
      setNotice(message);
      // Nothing is patched from a write's echo — the row's state after either
      // act is the server's answer, so both end in a RE-READ. NOT awaited: a
      // rejected re-read is that query's own error state, and awaiting it here
      // would render `blockers.action_failed` over a write the server accepted.
      void query.refetch();
    });
  };

  /** Send one reply — words or a pick — and say what it did, by its arm (#6726). */
  const answer = (blocker: ApiBlocker, reply: BlockerReply): void =>
    run(blocker, async () => {
      try {
        return answeredNotice(await answerBlocker(blocker.id, reply, token));
      } catch (err) {
        // The server took it; only its 200 could not be read. Say it landed, log why, and
        // let `run` re-read, rather than call an accepted answer a failure.
        if (!(err instanceof BlockerAnswerUnreadable)) throw err;
        console.error(err.message);
        return t('blockers.unread_notice');
      }
    });

  const withdraw = (blocker: ApiBlocker): void =>
    run(blocker, async () => {
      await withdrawBlocker(blocker.id, token);
      return t('blockers.withdrawn_notice');
    });

  return (
    <section class="page">
      <PageHeader
        title={t('blockers.title')}
        desc={t('blockers.subtitle')}
        actions={
          <label class="blockers__show-closed" for="blockers-show-closed">
            <input
              id="blockers-show-closed"
              type="checkbox"
              checked={!openOnly()}
              onChange={(event) => setOpenOnly(!event.currentTarget.checked)}
            />
            {t('blockers.show_closed')}
          </label>
        }
      />

      <Show when={token !== null} fallback={<EmptyState message={t('blockers.no_session')} />}>
        <Show when={focusId() !== null}>
          <div class="alert" role="note">
            <p class="alert__desc">{t('blockers.focused_notice')}</p>
            <button
              type="button"
              class={`${buttonClass({ variant: 'outline', size: 'sm' })} blocker-alert__action`}
              onClick={() => setParams({ blocker: undefined }, { replace: true })}
            >
              {t('blockers.focused_clear')}
            </button>
          </div>
        </Show>

        {/* QueryBoundary, not a bare Show: loading must not read as "empty".
            A deep link keeps the boundary out of its empty branch on purpose —
            with a named id, zero rows means "not in THIS list", not "none". */}
        <QueryBoundary
          query={query}
          isEmpty={(rows) => rows.length === 0 && focusId() === null}
          loading={<Skeleton height="14rem" />}
          empty={
            <EmptyState message={t(openOnly() ? 'blockers.empty' : 'blockers.empty_closed')} />
          }
        >
          {(rows) => (
            <BlockerFeed
              focus={focusBlockers(rows, focusId())}
              isBusy={act.isBusy}
              keys={keys.data}
              onAnswer={answer}
              onWithdraw={withdraw}
              onWiden={openOnly() ? () => setOpenOnly(false) : null}
            />
          )}
        </QueryBoundary>
      </Show>

      <Toast message={act.error()} tone="error" onDismiss={act.clearError} />
      <Toast message={notice()} tone="success" onDismiss={() => setNotice(null)} />
    </section>
  );
}
